
import React, { useMemo } from "react";
import { useTranslations } from "use-intl";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ConfirmPaymentModal from "./confirmation/ConfirmPaymentModal";
import PaymentsUnsupportedModal from "./PaymentsUnsupportedModal";
import { isInAppPaymentsEnabled } from "@/config/features";
import { Invoice } from "@/types/payments";

interface PaymentDialogsProps {
  paymentDialogOpen: boolean;
  setPaymentDialogOpen: (open: boolean) => void;
  cancelDialogOpen: boolean;
  setCancelDialogOpen: (open: boolean) => void;
  actionInvoiceId: string | null;
  selectedInvoices: string[];
  invoices: Invoice[]; // Add invoices prop to get full invoice data
  processPayment: () => void;
  processCancellation: () => void;
  refreshData?: () => Promise<void>;
}

const PaymentDialogs = ({
  paymentDialogOpen,
  setPaymentDialogOpen,
  cancelDialogOpen,
  setCancelDialogOpen,
  actionInvoiceId,
  selectedInvoices,
  invoices,
  processPayment,
  processCancellation,
  refreshData,
}: PaymentDialogsProps) => {
  const t = useTranslations("consultantPayments.dialogs");
  // Get the full invoice objects for selected invoices - memoized to prevent infinite loops
  const selectedInvoiceObjects = useMemo(() =>
    invoices.filter(invoice => selectedInvoices.includes(invoice.id)),
    [invoices, selectedInvoices]
  );

  return (
    <>
      {isInAppPaymentsEnabled() ? (
        <ConfirmPaymentModal
          open={paymentDialogOpen}
          onOpenChange={setPaymentDialogOpen}
          selectedInvoices={selectedInvoiceObjects}
          actionInvoiceId={actionInvoiceId}
          onConfirmPayment={processPayment}
          refreshData={refreshData}
        />
      ) : (
        <PaymentsUnsupportedModal
          open={paymentDialogOpen}
          onOpenChange={setPaymentDialogOpen}
        />
      )}

      {/* Cancel Dialog */}
      <AlertDialog 
        open={cancelDialogOpen} 
        onOpenChange={setCancelDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cancelTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {actionInvoiceId
                ? t("cancelOne", { id: actionInvoiceId })
                : t("cancelMany", { count: selectedInvoices.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("keep")}</AlertDialogCancel>
            <AlertDialogAction onClick={processCancellation} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t(actionInvoiceId ? "confirmOne" : "confirmMany")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PaymentDialogs;
